/**
 * WB Agent API client — thin wrapper around the wb_agent HTTP routes.
 *
 * Auth is handled the same way as LlmProxyClient: Basic auth header with
 * entity/project passed as query or body params, credentials: 'include'
 * for cookie-based sessions, and X-Origin for Gorilla's session validation.
 */

import {getCookie} from '@wandb/weave/common/util/cookie';

import type {components, operations, paths} from './generated/wb-agent-types';

// =============================================================================
// Types — generated from services/wb_agent/openapi.json
// =============================================================================

export type TurnState = components['schemas']['TurnState'];
export type SandboxProviderId = components['schemas']['SandboxProviderId'];

export const TERMINAL_STATES: TurnState[] = [
  'completed',
  'errored',
  'cancelled',
];

export type MessageRecord = components['schemas']['MessageRecord'];
export type ToolCall = components['schemas']['ToolCall'];
export type ToolCallInvocationRecord =
  components['schemas']['ToolCallInvocationRecord'];
export type ToolCallResponseRecord =
  components['schemas']['ToolCallResponseRecord'];
export type ToolCallRecord = NonNullable<
  components['schemas']['TurnResponse']['tool_calls']
>[number];
export type TurnResponse = components['schemas']['TurnResponse'];
export type CreateTurnRequest = components['schemas']['CreateTurnRequest'];
export type CloneTurnResponse = components['schemas']['CloneTurnResponse'];
export type ThreadResponse = components['schemas']['ThreadResponse'];
export type UpdateThreadRequest = components['schemas']['UpdateThreadRequest'];
export type GeneratedThreadTitleResponse =
  components['schemas']['GeneratedThreadTitleResponse'];
export type SetFeedbackRequest = components['schemas']['SetFeedbackRequest'];
export type IsAvailableResponse = components['schemas']['IsAvailableResponse'];

// Project memory.
export type ProjectMemory = components['schemas']['ProjectMemoryResponse'];
export type UpdateProjectMemoryRequest =
  components['schemas']['UpdateProjectMemoryRequest'];
export type DeleteProjectMemoryRequest =
  components['schemas']['DeleteProjectMemoryRequest'];

export type {AgentQuestion, MultipleChoiceQuestion} from './agentQuestions';
export type {
  NetworkAccessRequest,
  TurnPermissions,
  TurnPermissionsPatch,
} from './networkAccess';

// =============================================================================
// Route-level type safety — auto-derived from the generated paths/operations
// =============================================================================

/** Extract the first 2xx JSON response type from an operation. */
type SuccessResponseOf<R> = R extends {
  200: {content: {'application/json': infer T}};
}
  ? T
  : R extends {201: {content: {'application/json': infer T}}}
    ? T
    : R extends {202: {content: {'application/json': infer T}}}
      ? T
      : never;
type SuccessResponse<Op> = Op extends {responses: infer R}
  ? SuccessResponseOf<R>
  : never;

/**
 * Query params for an operation. Includes optional `entity` and `project`
 * keys so callers can override the client's constructor scope per call.
 */
type QueryParams<Op> = Op extends {parameters: {query?: infer Q}}
  ? NonNullable<Q>
  : Record<string, never>;

/** JSON request body type. */
type RequestBody<Op> = Op extends {
  requestBody: {content: {'application/json': infer B}};
}
  ? B
  : never;

/** Strip the /api/v1 prefix that the client prepends automatically. */
type StripApiPrefix<P extends string> = P extends `/api/v1${infer Rest}`
  ? Rest
  : never;

/** True when a path contains {param} segments (dynamic routes). */
type HasPathParams<P extends string> = P extends `${string}{${string}}${string}`
  ? true
  : false;

/**
 * GET route map — auto-derived from paths with a `get` operation and no path
 * parameters.  Maps stripped path → { response, params }.
 */
type GETRoutes = {
  [P in keyof paths & string as HasPathParams<P> extends true
    ? never
    : paths[P] extends {get: unknown}
      ? StripApiPrefix<P>
      : never]: paths[P] extends {get: infer Op}
    ? {response: SuccessResponse<Op>; params: QueryParams<Op>}
    : never;
};

/**
 * POST route map — auto-derived from paths with a `post` operation and no
 * path parameters.  Maps stripped path → { response, body, params }.
 */
type POSTRoutes = {
  [P in keyof paths & string as HasPathParams<P> extends true
    ? never
    : paths[P] extends {post: unknown}
      ? StripApiPrefix<P>
      : never]: paths[P] extends {post: infer Op}
    ? {
        response: SuccessResponse<Op>;
        body: RequestBody<Op>;
        params: QueryParams<Op>;
      }
    : never;
};

/**
 * PATCH route map. Auto-derived from paths with a `patch` operation and no
 * path parameters.  Maps stripped path → { response, body, params }.
 */
type PATCHRoutes = {
  [P in keyof paths & string as HasPathParams<P> extends true
    ? never
    : paths[P] extends {patch: unknown}
      ? StripApiPrefix<P>
      : never]: paths[P] extends {patch: infer Op}
    ? {
        response: SuccessResponse<Op>;
        body: RequestBody<Op>;
        params: QueryParams<Op>;
      }
    : never;
};

/**
 * Query parameters accepted by GET /turns. `entity` and `project` default to
 * the client's constructor scope; callers may override per call (pass
 * `undefined` to opt out of scoping).
 */
export type QueryTurnsParams = QueryParams<
  operations['query_turns_api_v1_turns_get']
>;

// =============================================================================
// Errors
// =============================================================================

/**
 * WbAgentApiError is what a non-2xx response throws. Every route answers a
 * refusal with an `{error: {code, message}}` envelope, so a caller can branch on
 * the reason it names rather than on the status alone.
 *
 * The message is unchanged from a plain throw, so a caller reading `.message` or
 * testing `instanceof Error` behaves as before.
 */
export class WbAgentApiError extends Error {
  readonly status: number;
  /** code is the envelope's `error.code`, or null when the body carried none. */
  readonly code: string | null;
  /** detail is the envelope's `error.message`, safe to show a user. */
  readonly detail: string | null;

  constructor(status: number, body: string) {
    super(`WbAgent API ${status}: ${body}`);
    this.name = 'WbAgentApiError';
    this.status = status;
    const {code, message} = parseErrorEnvelope(body);
    this.code = code;
    this.detail = message;
  }
}

function parseErrorEnvelope(body: string): {
  code: string | null;
  message: string | null;
} {
  const empty = {code: null, message: null};

  let error: unknown;
  try {
    ({error} = JSON.parse(body) as {error?: unknown});
  } catch {
    return empty;
  }

  if (typeof error !== 'object' || error === null) {
    return empty;
  }

  const {code, message} = error as {code?: unknown; message?: unknown};
  return {
    code: typeof code === 'string' ? code : null,
    message: typeof message === 'string' ? message : null,
  };
}

// =============================================================================
// Client
// =============================================================================

export interface WbAgentClientConfig {
  baseUrl: string; // e.g. "http://localhost:8090"
  entity?: string;
  project?: string;
}

export class WbAgentClient {
  private readonly baseUrl: URL;
  readonly entity: string | undefined;
  readonly project: string | undefined;

  constructor(config: WbAgentClientConfig) {
    this.baseUrl = new URL(config.baseUrl, window.location.origin);
    if (
      this.baseUrl.protocol !== 'http:' &&
      this.baseUrl.protocol !== 'https:'
    ) {
      throw new TypeError('WbAgent API base URL must use HTTP or HTTPS');
    }
    this.baseUrl.pathname = this.baseUrl.pathname.replace(/\/+$/, '');
    this.baseUrl.search = '';
    this.baseUrl.hash = '';
    this.entity = config.entity;
    this.project = config.project;
  }

  /**
   * Stable identity of what this client talks to: normalized base URL plus
   * the entity/project scope. Two clients with equal keys make identical
   * requests, so cached state may be shared between them; use it to key
   * caches so state from one backend or scope never serves another.
   */
  key(): string {
    return JSON.stringify([this.baseUrl.href, this.entity, this.project]);
  }

  // ── Helpers ──────────────────────────────────────────────────────────

  private buildHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      Authorization: 'Basic ' + btoa(':'),
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Origin': window.location.origin,
    };
    if (getCookie('use_admin_privileges') === 'true') {
      headers['use-admin-privileges'] = 'true';
    }
    return headers;
  }

  /**
   * scopedUrl builds an `/api/v1` URL with query params. Caller params
   * override constructor scope; pass `undefined` to opt out.
   */
  private scopedUrl(path: string, params: Record<string, unknown> = {}): URL {
    const merged = {entity: this.entity, project: this.project, ...params};

    const url = new URL(this.baseUrl);
    const basePath = url.pathname.replace(/\/+$/, '');
    url.pathname = `${basePath}/api/v1${path}`;
    for (const [key, value] of Object.entries(merged)) {
      if (value != null) {
        url.searchParams.set(key, String(value));
      }
    }
    return url;
  }

  private async request<T>(
    method: string,
    url: URL,
    body?: unknown,
    signal?: AbortSignal
  ): Promise<T> {
    // oxlint-disable-next-line wandb/no-unprefixed-urls
    const response = await fetch(url, {
      method,
      credentials: 'include',
      headers: this.buildHeaders(),
      body: body ? JSON.stringify(body) : undefined,
      signal,
    });

    if (!response.ok) {
      let errorBody: string;
      try {
        errorBody = await response.text();
      } catch {
        errorBody = response.statusText;
      }
      throw new WbAgentApiError(response.status, errorBody);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  }

  // ── Generic HTTP methods ───────────────────────────────────────────

  /** Typed overload — fixed paths, fully inferred response & params. */
  get<P extends keyof GETRoutes & string>(
    path: P,
    params?: GETRoutes[P]['params'],
    signal?: AbortSignal
  ): Promise<GETRoutes[P]['response']>;
  /** Fallback overload — dynamic paths, caller specifies T. */
  get<T>(
    path: string,
    params?: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<T>;
  async get(
    path: string,
    params?: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<unknown> {
    return this.request('GET', this.scopedUrl(path, params), undefined, signal);
  }

  /** Return raw streaming responses, e.g. text/event-stream SSE. */
  async stream(
    path: string,
    params?: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<Response> {
    // oxlint-disable-next-line wandb/no-unprefixed-urls
    const response = await fetch(this.scopedUrl(path, params), {
      method: 'GET',
      credentials: 'include',
      headers: this.buildHeaders(),
      signal,
    });
    if (!response.ok) {
      throw new WbAgentApiError(response.status, await response.text());
    }
    return response;
  }

  /** Typed overload — fixed paths, fully inferred response, body & params. */
  post<P extends keyof POSTRoutes & string>(
    path: P,
    body?: POSTRoutes[P]['body'],
    params?: POSTRoutes[P]['params'],
    signal?: AbortSignal
  ): Promise<POSTRoutes[P]['response']>;
  /** Fallback overload — dynamic paths, caller specifies T. */
  post<T>(
    path: string,
    body?: unknown,
    params?: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<T>;
  async post(
    path: string,
    body?: unknown,
    params?: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<unknown> {
    return this.request('POST', this.scopedUrl(path, params), body, signal);
  }

  /** Typed overload — fixed paths, fully inferred response, body & params. */
  patch<P extends keyof PATCHRoutes & string>(
    path: P,
    body?: PATCHRoutes[P]['body'],
    params?: PATCHRoutes[P]['params'],
    signal?: AbortSignal
  ): Promise<PATCHRoutes[P]['response']>;
  /** Fallback overload — dynamic paths, caller specifies T. */
  patch<T>(
    path: string,
    body?: unknown,
    params?: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<T>;
  async patch(
    path: string,
    body?: unknown,
    params?: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<unknown> {
    return this.request('PATCH', this.scopedUrl(path, params), body, signal);
  }
}
